import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { EditIcon, DeleteIcon, AddIcon } from '../components/Icons';
import './AdminCategories.css';

const LOCALES = [
  { code: 'lt', label: 'Lietuvių' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

function AdminCategories() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [activeLang, setActiveLang] = useState('lt');
  const [editForm, setEditForm] = useState({
    name: '',
    name_en: '',
    name_de: '',
    description: '',
    description_en: '',
    description_de: '',
    parent_id: null,
    sort_order: 0,
    is_active: true,
    image: null
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all', // all, active, inactive
  });

  const { data: categoriesData, isLoading } = useQuery({
    queryKey: ['admin-categories', page, limit, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit === 'all' ? '999999' : limit.toString(),
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status !== 'all') params.append('is_active', filters.status === 'active' ? '1' : '0');

      const res = await apiClient.get(`/admin/categories?${params.toString()}`);
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (formData) => {
      return apiClient.post('/admin/categories', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-categories']);
      setShowCreateModal(false);
      setEditingCategory(null);
      setEditForm({
        name: '',
        name_en: '',
        name_de: '',
        description: '',
        description_en: '',
        description_de: '',
        parent_id: null,
        sort_order: 0,
        is_active: true,
        image: null
      });
      setImagePreview(null);
      success('Kategorija sėkmingai sukurta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida kuriant kategoriją');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, formData }) => {
      return apiClient.put(`/admin/categories/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-categories']);
      setShowCreateModal(false);
      setEditingCategory(null);
      setEditForm({
        name: '',
        name_en: '',
        name_de: '',
        description: '',
        description_en: '',
        description_de: '',
        parent_id: null,
        sort_order: 0,
        is_active: true,
        image: null
      });
      setImagePreview(null);
      success('Kategorija sėkmingai atnaujinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atnaujinant kategoriją');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      return apiClient.delete(`/admin/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-categories']);
      success('Kategorija pašalinta');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida šalinant kategoriją');
    },
  });

  const handleEdit = (category) => {
    setEditingCategory(category);
    setEditForm({
      name: category.name || '',
      name_en: category.name_en || '',
      name_de: category.name_de || '',
      description: category.description || '',
      description_en: category.description_en || '',
      description_de: category.description_de || '',
      parent_id: category.parent_id || null,
      sort_order: category.sort_order || 0,
      is_active: category.is_active !== undefined ? category.is_active : true,
      image: null
    });
    setImagePreview(category.image ? `${process.env.REACT_APP_API_URL || ''}${category.image}` : null);
    setShowCreateModal(true);
  };

  const handleNew = () => {
    setEditingCategory(null);
    setEditForm({
      name: '',
      name_en: '',
      name_de: '',
      description: '',
      description_en: '',
      description_de: '',
      parent_id: null,
      sort_order: 0,
      is_active: true,
      image: null
    });
    setImagePreview(null);
    setShowCreateModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditForm({ ...editForm, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (!editForm.name || !editForm.name.trim()) {
      showError('Pavadinimas (lietuvių) yra privalomas');
      return;
    }

    const formData = new FormData();
    formData.append('name', editForm.name.trim());
    formData.append('name_en', (editForm.name_en ?? '').toString().trim());
    formData.append('name_de', (editForm.name_de ?? '').toString().trim());
    formData.append('description', (editForm.description ?? '').toString().trim());
    formData.append('description_en', (editForm.description_en ?? '').toString().trim());
    formData.append('description_de', (editForm.description_de ?? '').toString().trim());
    if (editForm.parent_id) {
      formData.append('parent_id', editForm.parent_id);
    }
    formData.append('sort_order', editForm.sort_order || 0);
    formData.append('is_active', editForm.is_active);

    if (editForm.image) {
      formData.append('image', editForm.image);
    }

    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleRemoveImage = () => {
    setEditForm({ ...editForm, image: null });
    setImagePreview(null);
    if (editingCategory) {
      // Pažymėti, kad reikia pašalinti nuotrauką
      const formData = new FormData();
      formData.append('remove_image', 'true');
      updateMutation.mutate({ id: editingCategory.id, formData });
    }
  };

  const renderCategoryTree = (categories, level = 0) => {
    if (!categories || categories.length === 0) return null;

    return (
      <ul className={`category-tree level-${level}`}>
        {categories.map((category) => (
          <li key={category.id} className="category-item">
            <div className="category-row">
              <div className="category-info">
                <div className="category-name">
                  {'  '.repeat(level)}
                  {category.image && (
                    <img 
                      src={`${process.env.REACT_APP_API_URL || ''}${category.image}`} 
                      alt={category.name}
                      className="category-image-small"
                    />
                  )}
                  <span>{category.name}</span>
                  {!category.is_active && (
                    <span className="status-badge inactive">Neaktyvus</span>
                  )}
                </div>
                {category.description && (
                  <div className="category-description">{category.description}</div>
                )}
                <div className="category-meta">
                  <span>Prekių: {category.product_count || 0}</span>
                  <span>Eilės nr.: {category.sort_order || 0}</span>
                </div>
              </div>
              <div className="category-actions">
                <button
                  onClick={() => handleEdit(category)}
                  className="btn-icon btn-icon-secondary"
                  title="Redaguoti"
                >
                  <EditIcon />
                </button>
                <button
                  onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      title: 'Pašalinti kategoriją',
                      message: `Ar tikrai norite pašalinti kategoriją "${category.name}"?`,
                      type: 'danger',
                      confirmText: 'Pašalinti',
                      onConfirm: () => {
                        deleteMutation.mutate(category.id);
                        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
                      },
                      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
                    });
                  }}
                  className="btn-icon btn-icon-danger"
                  title="Pašalinti"
                  disabled={category.product_count > 0 || (category.children && category.children.length > 0)}
                >
                  <DeleteIcon />
                </button>
              </div>
            </div>
            {category.children && category.children.length > 0 && (
              <div className="category-children">
                {renderCategoryTree(category.children, level + 1)}
              </div>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (isLoading) {
    return <div className="admin-categories-page container">Kraunama...</div>;
  }

  return (
    <div className="admin-categories-page container">
      <div className="categories-header">
        <h1>Prekių kategorijos</h1>
        <button onClick={handleNew} className="btn btn-primary btn-sm">
          <AddIcon />
          Pridėti kategoriją
        </button>
      </div>

      {/* Filtrai */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Paieška:</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => {
                setFilters({ ...filters, search: e.target.value });
                setPage(1);
              }}
              className="input"
              placeholder="Ieškoti pagal pavadinimą..."
            />
          </div>
          <div className="filter-group">
            <label>Statusas:</label>
            <select
              value={filters.status}
              onChange={(e) => {
                setFilters({ ...filters, status: e.target.value });
                setPage(1);
              }}
              className="input"
            >
              <option value="all">Visi</option>
              <option value="active">Aktyvūs</option>
              <option value="inactive">Neaktyvūs</option>
            </select>
          </div>
          <div className="filter-group">
            <button
              onClick={() => {
                setFilters({ search: '', status: 'all' });
                setPage(1);
              }}
              className="btn btn-secondary"
            >
              Išvalyti
            </button>
          </div>
        </div>
      </div>

      {categoriesData?.tree && categoriesData.tree.length > 0 ? (
        <>
          <div className="categories-tree">
            {renderCategoryTree(categoriesData.tree)}
          </div>
          {categoriesData?.pagination && (
            <Pagination
              page={categoriesData.pagination.page}
              totalPages={categoriesData.pagination.pages}
              totalItems={categoriesData.pagination.total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>Kategorijų nėra. Sukurkite pirmąją kategoriją.</p>
        </div>
      )}

      {/* Redagavimo/Sukūrimo modalas */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setEditingCategory(null);
          setImagePreview(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingCategory ? 'Redaguoti kategoriją' : 'Nauja kategorija'}</h2>

            <p className="admin-lang-hint">Pavadinimas ir aprašymas – bent viena kalba (LT privalomas).</p>
            <div className="admin-lang-tabs">
              {LOCALES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  className={`admin-lang-tab ${activeLang === code ? 'active' : ''}`}
                  onClick={() => setActiveLang(code)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="form-group">
              <label>Pavadinimas ({LOCALES.find(l => l.code === activeLang)?.label}) {activeLang === 'lt' ? '*' : ''}</label>
              <input
                type="text"
                value={editForm[activeLang === 'lt' ? 'name' : `name_${activeLang}`] ?? ''}
                onChange={(e) => setEditForm({
                  ...editForm,
                  ...(activeLang === 'lt' ? { name: e.target.value } : { [`name_${activeLang}`]: e.target.value })
                })}
                className="input"
                required={activeLang === 'lt'}
              />
            </div>

            <div className="form-group">
              <label>Aprašymas ({LOCALES.find(l => l.code === activeLang)?.label})</label>
              <textarea
                value={editForm[activeLang === 'lt' ? 'description' : `description_${activeLang}`] ?? ''}
                onChange={(e) => setEditForm({
                  ...editForm,
                  ...(activeLang === 'lt' ? { description: e.target.value } : { [`description_${activeLang}`]: e.target.value })
                })}
                className="input"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Tėvinė kategorija</label>
              <select
                value={editForm.parent_id || ''}
                onChange={(e) => setEditForm({ ...editForm, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                className="input"
              >
                <option value="">Nėra (pagrindinė kategorija)</option>
                {categoriesData?.categories
                  .filter(cat => !editingCategory || cat.id !== editingCategory.id)
                  .map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Rikiavimo eilės numeris</label>
                <input
                  type="number"
                  value={editForm.sort_order}
                  onChange={(e) => setEditForm({ ...editForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="input"
                />
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  />
                  <span>Aktyvus</span>
                </label>
              </div>
            </div>

            <div className="form-group">
              <label>Nuotrauka</label>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="btn btn-sm btn-danger"
                  >
                    Pašalinti
                  </button>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="input"
              />
            </div>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingCategory(null);
                  setImagePreview(null);
                }}
                className="btn btn-secondary"
              >
                Atšaukti
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={createMutation.isLoading || updateMutation.isLoading || !editForm.name || !editForm.name.trim()}
                className="btn btn-primary"
              >
                {createMutation.isLoading || updateMutation.isLoading ? 'Išsaugoma...' : 'Išsaugoti'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

export default AdminCategories;
